import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  apiFetch,
  fetchBaselineApprovalNotificationCount,
  fetchReviewQueueAccess,
} from "../../services/api";
import {
  clearEnrichmentClientJobCache,
  isEnrichmentJobType,
  PENDING_JOBS_KEY,
  readPendingEnrichmentJob,
} from "../../utils/enrichmentClientCache";
import { enrichmentJobNeedsReview } from "../../utils/enrichmentReviewQueue";
import { currentUserId, userCanContribute } from "../../utils/userRoles";
import { isOpeningSessionJob } from "../../utils/isOpeningSessionJob";

/** Same window as useAiJobState — drop orphan optimistic rows from merge. */
const PENDING_MAX_AGE_MS = 5 * 60 * 1000;

function isEnrichmentJob(j) {
  return isEnrichmentJobType(j);
}

function isCompletedJob(j) {
  return String(j?.status || "").toLowerCase() === "completed";
}

function jobQueuedByUserId(job) {
  const payload = job?.payload;
  if (payload && typeof payload === "object") {
    return String(payload.queued_by_user_id || "").trim();
  }
  return String(job?.queued_by_user_id || "").trim();
}

function jobOwnedByCurrentUser(job) {
  const actor = currentUserId();
  if (!actor) return false;
  const owner = jobQueuedByUserId(job);
  return Boolean(owner) && owner === actor;
}

function filterJobsForCurrentUser(jobs) {
  return (jobs || []).filter(jobOwnedByCurrentUser);
}

/**
 * Drop pending_jobs entries whose id already exists on the server (any status),
 * matching useAiJobState.cleanupOptimisticJobs so completed jobs do not leave
 * stale optimistic rows in localStorage.
 */
function cleanupPendingJobsAgainstServer(allServerJobs) {
  try {
    const list = Array.isArray(allServerJobs) ? allServerJobs : [];
    const realIds = new Set(
      list.map((j) => String(j?.id ?? "").trim()).filter(Boolean)
    );
    const pending = JSON.parse(localStorage.getItem(PENDING_JOBS_KEY) || "[]");
    if (!Array.isArray(pending)) return;
    const remaining = pending.filter((job) => {
      if (!jobOwnedByCurrentUser(job)) return false;
      const id = String(job?.id ?? "").trim();
      // Drop only when the server already returned this id (dedupe). Keep placeholders
      // while the queue API is still catching up — never wipe all enrichment rows when
      // the jobs list is temporarily empty.
      if (id && realIds.has(id)) return false;
      return true;
    });
    if (remaining.length !== pending.length) {
      localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(remaining));
    }
  } catch {
    /* ignore */
  }
}

/** Merge client optimistic enrichment rows until the jobs API returns the same id. */
function mergePendingEnrichmentFromStorage(serverEnrichJobs) {
  const out = [...(serverEnrichJobs || [])];
  const seen = new Set(
    out.map((j) => String(j?.id || "").trim()).filter(Boolean)
  );
  const now = Date.now();
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_JOBS_KEY) || "[]");
    if (!Array.isArray(pending)) return sortEnrichmentJobs(out);
    pending.filter(isEnrichmentJob).forEach((j) => {
      const id = String(j?.id || "").trim();
      if (!id || seen.has(id)) return;
      const ts = new Date(j.created_at || j.updated_at || 0).getTime();
      if (!Number.isFinite(ts) || now - ts > PENDING_MAX_AGE_MS) return;
      seen.add(id);
      out.push({
        ...j,
        job_type: String(j?.job_type || "application_enrichment"),
        status: String(j?.status || "pending"),
      });
    });
  } catch {
    /* ignore */
  }
  return sortEnrichmentJobs(out);
}

function sortEnrichmentJobs(jobs) {
  return [...jobs].sort((a, b) => {
    const at = new Date(a?.created_at || a?.updated_at || 0).getTime();
    const bt = new Date(b?.created_at || b?.updated_at || 0).getTime();
    return bt - at;
  });
}

const V2EnrichmentReviewContext = createContext(null);

const JOBS_REFRESH_DEBOUNCE_MS = 800;
const BASELINE_APPROVAL_QUEUE_CHANGED = "baselineApprovalQueueChanged";
const ENRICHMENT_JOBS_ACTIVE_REFRESH_MS = 10000;
const ENRICHMENT_JOBS_IDLE_REFRESH_MS = 60000;
const BASELINE_APPROVAL_REFRESH_MS = 120000;

function isDocumentVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function V2EnrichmentReviewProvider({ children }) {
  const [enrichmentJobs, setEnrichmentJobs] = useState([]);
  const [pendingBaselineApprovalCount, setPendingBaselineApprovalCount] = useState(0);
  const [canAccessApprovals, setCanAccessApprovals] = useState(false);
  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(null);
  const baselineApprovalRefreshInFlightRef = useRef(null);

  const fetchEnrichmentJobsNow = useCallback(async () => {
    if (!userCanContribute()) {
      setEnrichmentJobs([]);
      return;
    }
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const run = (async () => {
      try {
        const [enrichRes, refineRes, ingestRes] = await Promise.all([
          apiFetch("/api/jobs?limit=15&job_type=application_enrichment"),
          apiFetch("/api/jobs?limit=15&job_type=entity_refine"),
          apiFetch("/api/jobs?limit=15&job_type=knowledge_ingestion"),
        ]);
        const list = [];
        if (enrichRes?.ok) {
          const data = await enrichRes.json();
          if (Array.isArray(data?.jobs)) list.push(...data.jobs);
        }
        if (refineRes?.ok) {
          const data = await refineRes.json();
          if (Array.isArray(data?.jobs)) list.push(...data.jobs);
        }
        if (ingestRes?.ok) {
          const data = await ingestRes.json();
          if (Array.isArray(data?.jobs)) list.push(...data.jobs);
        }
        if (!enrichRes?.ok && !refineRes?.ok && !ingestRes?.ok) return;
        cleanupPendingJobsAgainstServer(list);
        const enrichJobsFromApi = sortEnrichmentJobs(
          filterJobsForCurrentUser(list.filter(isEnrichmentJob))
        );
        setEnrichmentJobs(mergePendingEnrichmentFromStorage(enrichJobsFromApi));
      } catch {
        /* ignore */
      }
    })();
    refreshInFlightRef.current = run;
    try {
      await run;
    } finally {
      if (refreshInFlightRef.current === run) {
        refreshInFlightRef.current = null;
      }
    }
  }, []);

  const refreshBaselineApprovalCount = useCallback(async () => {
    if (!canAccessApprovals) {
      setPendingBaselineApprovalCount(0);
      return;
    }
    if (baselineApprovalRefreshInFlightRef.current) {
      return baselineApprovalRefreshInFlightRef.current;
    }
    const run = (async () => {
      try {
        const count = await fetchBaselineApprovalNotificationCount();
        setPendingBaselineApprovalCount(count);
      } catch {
        setPendingBaselineApprovalCount(0);
      }
    })();
    baselineApprovalRefreshInFlightRef.current = run;
    try {
      await run;
    } finally {
      if (baselineApprovalRefreshInFlightRef.current === run) {
        baselineApprovalRefreshInFlightRef.current = null;
      }
    }
  }, [canAccessApprovals]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const access = await fetchReviewQueueAccess();
        if (!cancelled) {
          setCanAccessApprovals(Boolean(access?.in_approver_group));
        }
      } catch {
        if (!cancelled) setCanAccessApprovals(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshEnrichmentJobs = useCallback(
    (options = {}) => {
      if (options?.immediate) {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
        return fetchEnrichmentJobsNow();
      }
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        void fetchEnrichmentJobsNow();
      }, JOBS_REFRESH_DEBOUNCE_MS);
    },
    [fetchEnrichmentJobsNow]
  );

  /** Merge pending_jobs into state immediately so the shell banner does not wait on /api/jobs. */
  const applyClientEnrichmentCache = useCallback((detailJobId) => {
    setEnrichmentJobs((prev) => {
      let merged = mergePendingEnrichmentFromStorage(prev);
      const jobId = String(detailJobId || "").trim();
      if (jobId && !merged.some((j) => String(j?.id || "").trim() === jobId)) {
        const stored = readPendingEnrichmentJob(jobId);
        merged = sortEnrichmentJobs([
          ...merged,
          stored || {
            id: jobId,
            job_type: "application_enrichment",
            status: "pending",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_optimistic: true,
          },
        ]);
      }
      return merged;
    });
  }, []);

  /** Keep inbox badges in sync while the user is actively reviewing. */
  const updateEnrichmentJobPreview = useCallback((jobId, preview) => {
    const id = String(jobId || "").trim();
    if (!id || !preview) return;
    setEnrichmentJobs((prev) =>
      prev.map((j) => {
        if (String(j?.id || "").trim() !== id) return j;
        let doc = j.result;
        if (typeof doc === "string") {
          try {
            doc = JSON.parse(doc);
          } catch {
            doc = {};
          }
        }
        const base = doc && typeof doc === "object" ? { ...doc } : {};
        return { ...j, result: { ...base, preview } };
      })
    );
  }, []);

  useEffect(() => {
    const onQueueChanged = () => void refreshBaselineApprovalCount();
    window.addEventListener(BASELINE_APPROVAL_QUEUE_CHANGED, onQueueChanged);
    return () =>
      window.removeEventListener(BASELINE_APPROVAL_QUEUE_CHANGED, onQueueChanged);
  }, [refreshBaselineApprovalCount]);

  useEffect(() => {
    const onNewJobQueued = (ev) => {
      applyClientEnrichmentCache(ev?.detail?.jobId);
      // Soft sync with server — optimistic UI is already visible from localStorage.
      void refreshEnrichmentJobs();
    };
    const onAiProcessingStart = () => {
      applyClientEnrichmentCache();
    };
    const onAiProcessingStop = () => {
      void refreshEnrichmentJobs();
    };
    window.addEventListener("newJobQueued", onNewJobQueued);
    window.addEventListener("aiProcessingStart", onAiProcessingStart);
    window.addEventListener("aiProcessingStop", onAiProcessingStop);
    return () => {
      window.removeEventListener("newJobQueued", onNewJobQueued);
      window.removeEventListener("aiProcessingStart", onAiProcessingStart);
      window.removeEventListener("aiProcessingStop", onAiProcessingStop);
    };
  }, [applyClientEnrichmentCache, refreshEnrichmentJobs]);

  useEffect(() => {
    const onDatabaseChanged = () => {
      clearEnrichmentClientJobCache();
      setEnrichmentJobs([]);
      void refreshEnrichmentJobs();
      void refreshBaselineApprovalCount();
    };
    window.addEventListener("database-changed", onDatabaseChanged);
    return () => window.removeEventListener("database-changed", onDatabaseChanged);
  }, [refreshEnrichmentJobs, refreshBaselineApprovalCount]);

  const pendingReviewCount = useMemo(() => {
    return enrichmentJobs.filter(
      (j) => enrichmentJobNeedsReview(j) && !isOpeningSessionJob(j)
    ).length;
  }, [enrichmentJobs]);

  const hasPendingEnrichmentReview = pendingReviewCount > 0;

  const inboxNotificationCount = pendingReviewCount;

  const hasInboxNotifications = inboxNotificationCount > 0;

  const baselineApprovalNotificationCount = pendingBaselineApprovalCount;
  const hasBaselineApprovalNotifications = baselineApprovalNotificationCount > 0;

  const hasActiveEnrichmentProcessing = useMemo(() => {
    const activeInState = enrichmentJobs.some((j) => {
      if (isOpeningSessionJob(j)) return false;
      const st = String(j?.status || "").toLowerCase();
      return !["completed", "failed", "cancelled"].includes(st);
    });
    if (activeInState) return true;
    try {
      const pending = JSON.parse(localStorage.getItem(PENDING_JOBS_KEY) || "[]");
      if (!Array.isArray(pending)) return false;
      const now = Date.now();
      return pending.some((j) => {
        if (!isEnrichmentJob(j) || !jobOwnedByCurrentUser(j)) return false;
        if (isOpeningSessionJob(j)) return false;
        const st = String(j?.status || "").toLowerCase();
        if (["completed", "failed", "cancelled"].includes(st)) return false;
        const ts = new Date(j.created_at || j.updated_at || 0).getTime();
        if (!Number.isFinite(ts) || now - ts > PENDING_MAX_AGE_MS) return false;
        return true;
      });
    } catch {
      return false;
    }
  }, [enrichmentJobs]);

  useEffect(() => {
    const refreshJobsIfVisible = () => {
      if (!isDocumentVisible()) return;
      void refreshEnrichmentJobs({ immediate: true });
    };
    const refreshBaselineIfVisible = () => {
      if (!isDocumentVisible()) return;
      void refreshBaselineApprovalCount();
    };

    refreshJobsIfVisible();
    refreshBaselineIfVisible();

    let enrichmentTimer = null;
    const scheduleEnrichmentPoll = () => {
      if (enrichmentTimer) clearTimeout(enrichmentTimer);
      const delay = hasActiveEnrichmentProcessing
        ? ENRICHMENT_JOBS_ACTIVE_REFRESH_MS
        : ENRICHMENT_JOBS_IDLE_REFRESH_MS;
      enrichmentTimer = window.setTimeout(() => {
        refreshJobsIfVisible();
        scheduleEnrichmentPoll();
      }, delay);
    };
    scheduleEnrichmentPoll();

    const baselineIntervalId = window.setInterval(
      refreshBaselineIfVisible,
      BASELINE_APPROVAL_REFRESH_MS
    );

    const onFocus = () => {
      refreshJobsIfVisible();
      refreshBaselineIfVisible();
    };
    const onVisibility = () => {
      if (isDocumentVisible()) {
        refreshJobsIfVisible();
        refreshBaselineIfVisible();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (enrichmentTimer) clearTimeout(enrichmentTimer);
      window.clearInterval(baselineIntervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    hasActiveEnrichmentProcessing,
    refreshEnrichmentJobs,
    refreshBaselineApprovalCount,
  ]);

  const isEnrichmentJobPendingReview = useCallback(
    (job) => enrichmentJobNeedsReview(job),
    []
  );

  const value = useMemo(
    () => ({
      enrichmentJobs,
      pendingReviewCount,
      isEnrichmentJobPendingReview,
      hasPendingEnrichmentReview,
      inboxNotificationCount,
      hasInboxNotifications,
      pendingBaselineApprovalCount,
      baselineApprovalNotificationCount,
      hasBaselineApprovalNotifications,
      hasActiveEnrichmentProcessing,
      refreshEnrichmentJobs,
      refreshBaselineApprovalCount,
      updateEnrichmentJobPreview,
      applyClientEnrichmentCache,
    }),
    [
      enrichmentJobs,
      pendingReviewCount,
      isEnrichmentJobPendingReview,
      hasPendingEnrichmentReview,
      inboxNotificationCount,
      hasInboxNotifications,
      pendingBaselineApprovalCount,
      baselineApprovalNotificationCount,
      hasBaselineApprovalNotifications,
      hasActiveEnrichmentProcessing,
      refreshEnrichmentJobs,
      refreshBaselineApprovalCount,
      updateEnrichmentJobPreview,
      applyClientEnrichmentCache,
    ]
  );

  return (
    <V2EnrichmentReviewContext.Provider value={value}>{children}</V2EnrichmentReviewContext.Provider>
  );
}

export function useV2EnrichmentReview() {
  const ctx = useContext(V2EnrichmentReviewContext);
  if (!ctx) {
    throw new Error("useV2EnrichmentReview must be used within V2EnrichmentReviewProvider");
  }
  return ctx;
}
